import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import { gsap } from 'gsap';

const SceneContainer = styled.div`
  width: 100%;
  max-width: 1400px;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  color: #fff;
  padding: 2rem;
`;

const SceneTitle = styled.h2`
  font-size: 3rem;
  font-weight: 300;
  text-transform: uppercase;
  letter-spacing: 4px;
  margin: 0 0 1rem 0;
  text-align: center;
  background: linear-gradient(45deg, #fff, #00ff88);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 0 30px rgba(0, 255, 136, 0.5);
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.3rem;
  color: #00ff88;
  text-align: center;
  margin: 0 0 3rem 0;
  font-weight: 300;
  letter-spacing: 2px;
  opacity: 0.8;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 2rem;
  }
`;

const GardenPlatform = styled.div`
  width: 600px;
  height: 400px;
  background: linear-gradient(145deg, #1a1a1a, #0a0a0a);
  border: 3px solid #00ff88;
  border-radius: 20px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -10px;
    left: -10px;
    right: -10px;
    bottom: -10px;
    border-radius: 20px;
    background: linear-gradient(45deg, #00ff88, #00cc6a);
    opacity: 0.2;
    filter: blur(20px);
    animation: pulse 4s ease-in-out infinite;
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 0.2; transform: scale(1); }
    50% { opacity: 0.4; transform: scale(1.02); }
  }
  
  @media (max-width: 768px) {
    width: 90%;
    height: 300px;
  }
`;

const BustContainer = styled.div<{ isActive: boolean }>`
  position: absolute;
  width: 120px;
  height: 160px;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: all 0.4s ease;
  
  &:hover {
    transform: translateY(-10px) scale(1.05);
  }
  
  .bust {
    width: 80px;
    height: 100px;
    background: linear-gradient(145deg, #2a2a2a, #1a1a1a);
    border: 2px solid ${props => props.isActive ? '#00ff88' : 'rgba(0, 255, 136, 0.3)'};
    border-radius: 50% 50% 50% 50% / 60% 60% 40% 40%;
    position: relative;
    margin-bottom: 1rem;
    
    &::before {
      content: '';
      position: absolute;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      width: 40px;
      height: 40px;
      background: linear-gradient(45deg, #00ff88, #00cc6a);
      border-radius: 50%;
      opacity: ${props => props.isActive ? 1 : 0.3};
      transition: opacity 0.3s ease;
    }
    
    &::after {
      content: '';
      position: absolute;
      top: 70px;
      left: 50%;
      transform: translateX(-50%);
      width: 60px;
      height: 20px;
      background: linear-gradient(90deg, #00ff88, #00cc6a);
      border-radius: 10px;
      opacity: ${props => props.isActive ? 1 : 0.3};
      transition: opacity 0.3s ease;
    }
  }
  
  .bust-label {
    font-size: 0.9rem;
    color: #00ff88;
    text-align: center;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 1px;
    opacity: ${props => props.isActive ? 1 : 0.7};
    transition: opacity 0.3s ease;
  }
  
  .read-button {
    margin-top: 0.5rem;
    padding: 0.5rem 1rem;
    background: ${props => props.isActive ? '#00ff88' : 'transparent'};
    color: ${props => props.isActive ? '#000' : '#00ff88'};
    border: 1px solid #00ff88;
    border-radius: 15px;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      background: #00ff88;
      color: #000;
      transform: translateY(-2px);
    }
  }
`;

const WisdomGuide = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 2rem;
  background: rgba(0, 0, 0, 0.9);
  border: 2px solid #00ff88;
  border-radius: 15px;
  padding: 1.5rem;
  max-width: 400px;
  backdrop-filter: blur(10px);
  
  .guide-title {
    font-size: 1rem;
    color: #00ff88;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin: 0 0 1rem 0;
  }
  
  .guide-text {
    font-size: 0.9rem;
    color: #ccc;
    line-height: 1.6;
    margin: 0 0 1rem 0;
  }
  
  .guide-navigation {
    display: flex;
    gap: 1rem;
    justify-content: center;
    
    .nav-button {
      width: 30px;
      height: 30px;
      background: transparent;
      border: 1px solid #00ff88;
      border-radius: 50%;
      color: #00ff88;
      cursor: pointer;
      transition: all 0.3s ease;
      
      &:hover {
        background: #00ff88;
        color: #000;
        transform: scale(1.1);
      }
    }
  }
  
  @media (max-width: 768px) {
    position: relative;
    bottom: auto;
    left: auto;
    margin-top: 2rem;
    max-width: 100%;
  }
`;

const FloatingParticles = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: -1;
  
  .particle {
    position: absolute;
    width: 4px;
    height: 4px;
    background: #00ff88;
    border-radius: 50%;
    opacity: 0.6;
    animation: float-particle 8s ease-in-out infinite;
    
    &:nth-child(1) { top: 20%; left: 10%; animation-delay: 0s; }
    &:nth-child(2) { top: 40%; right: 15%; animation-delay: 2s; }
    &:nth-child(3) { top: 60%; left: 20%; animation-delay: 4s; }
    &:nth-child(4) { top: 80%; right: 25%; animation-delay: 6s; }
    &:nth-child(5) { top: 30%; left: 30%; animation-delay: 1s; }
    &:nth-child(6) { top: 70%; right: 35%; animation-delay: 3s; }
  }
  
  @keyframes float-particle {
    0%, 100% { transform: translateY(0px) scale(1); opacity: 0.3; }
    50% { transform: translateY(-30px) scale(1.2); opacity: 0.8; }
  }
`;

const GardenScene: React.FC = () => {
  const sceneRef = useRef<HTMLDivElement>(null);
  const [activeBust, setActiveBust] = useState<number | null>(null);

  const busts = [
    { id: 1, name: "Warrior", position: { top: '20%', left: '15%' } },
    { id: 2, name: "Scholar", position: { top: '30%', right: '20%' } },
    { id: 3, name: "Creator", position: { top: '60%', left: '25%' } }
  ];

  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    // Animate busts on scene enter
    gsap.fromTo('.bust-container', 
      { 
        opacity: 0, 
        y: 50,
        scale: 0.8
      },
      { 
        opacity: 1, 
        y: 0,
        scale: 1,
        duration: 1.2,
        ease: "power2.out",
        stagger: 0.3
      }
    );

  }, []);

  const handleBustClick = (bustId: number) => {
    setActiveBust(activeBust === bustId ? null : bustId);
  };

  return (
    <SceneContainer ref={sceneRef} className="scene">
      <FloatingParticles>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
      </FloatingParticles>
      
      <SceneTitle>Inspiration Garden</SceneTitle>
      <Subtitle>Where ingenuity overflows</Subtitle>
      
      <GardenPlatform>
        {busts.map((bust) => (
          <BustContainer
            key={bust.id}
            className="bust-container"
            style={bust.position}
            isActive={activeBust === bust.id}
            onClick={() => handleBustClick(bust.id)}
          >
            <div className="bust"></div>
            <div className="bust-label">{bust.name}</div>
            <button className="read-button">
              {activeBust === bust.id ? 'Read' : 'Read'}
            </button>
          </BustContainer>
        ))}
      </GardenPlatform>
      
      <WisdomGuide>
        <h3 className="guide-title">Wisdom Guide</h3>
        <p className="guide-text">
          I see you've made it to the Inspiration Garden. This place carries more purpose than simply being pleasing to the eye.
        </p>
        <div className="guide-navigation">
          <button className="nav-button">←</button>
          <button className="nav-button">→</button>
        </div>
      </WisdomGuide>
    </SceneContainer>
  );
};

export default GardenScene;
